const cloneSnapshot = (value) =>
  value == null ? value : JSON.parse(JSON.stringify(value));

const snapshotsMatch = (left, right) =>
  JSON.stringify(left) === JSON.stringify(right);

export default class UndoRedo {
  constructor(maxUndoSteps = 10) {
    this.position = -1;
    this.data = [];
    this.timestamps = [];
    this.maxUndoSteps = maxUndoSteps;
    this.lastGroupKey = null;
    this.lastPushTime = 0;
  }

  get getPosition() {
    return this.position;
  }

  get getData() {
    return this.data;
  }

  get top() {
    return this.data[this.position];
  }

  get canUndo() {
    return this.position > 0;
  }

  get canRedo() {
    return this.position >= 0 && this.position < this.data.length - 1;
  }

  get undoTimestamp() {
    return this.canUndo ? this.timestamps?.[this.position] || 0 : -Infinity;
  }

  get redoTimestamp() {
    return this.canRedo ? this.timestamps?.[this.position + 1] || 0 : Infinity;
  }

  get getPreviousState() {
    if (!this.canUndo) return this.top;
    this.setPosition(this.position - 1);
    return cloneSnapshot(this.top);
  }

  get getNextState() {
    if (!this.canRedo) return this.top;
    this.setPosition(this.position + 1);
    return cloneSnapshot(this.top);
  }

  setPosition(position) {
    this.position = Math.max(-1, Math.min(position, this.data.length - 1));
    this.breakCoalescing();
  }

  breakCoalescing() {
    this.lastGroupKey = null;
    this.lastPushTime = 0;
  }

  rewriteSnapshots(rewrite) {
    if (typeof rewrite !== "function") return;
    this.data = this.data.map((snapshot, index) =>
      cloneSnapshot(rewrite(cloneSnapshot(snapshot), index)),
    );
  }

  push(data, { groupKey = null, coalesceMs = 500 } = {}) {
    const snapshot = cloneSnapshot(data);
    if (snapshot == null) return false;
    if (!Array.isArray(this.timestamps)) this.timestamps = [];

    const now = Date.now();
    const shouldCoalesce =
      groupKey &&
      this.position === this.data.length - 1 &&
      this.lastGroupKey === groupKey &&
      now - this.lastPushTime <= coalesceMs;

    if (shouldCoalesce) {
      this.data[this.position] = snapshot;
      this.timestamps[this.position] = now;
      this.lastPushTime = now;
      return true;
    }

    if (snapshotsMatch(this.top, snapshot)) return false;

    this.data = [...this.data.slice(0, this.position + 1), snapshot];
    this.timestamps = [
      ...this.timestamps.slice(0, this.position + 1),
      now,
    ];
    this.position = this.data.length - 1;
    this.lastGroupKey = groupKey;
    this.lastPushTime = now;

    const maxSnapshots = this.maxUndoSteps + 1;
    if (this.data.length > maxSnapshots) {
      const overflow = this.data.length - maxSnapshots;
      this.data = this.data.slice(overflow);
      this.timestamps = this.timestamps.slice(overflow);
      this.position -= overflow;
    }

    return true;
  }
}
